import React from 'react'
import styled from '@emotion/styled'
import { GridApi, IHeaderParams, IRowNode } from 'ag-grid-community'
import { Tooltip } from 'view/components/tooltip'
import { Locale, Translation } from 'locale'
import * as constants from 'view/styles/constants'
import { Checkbox } from 'view/components/inputs'
import { Selectbox } from 'view/components/options'
import { setNodesSelected, TypedRowNode } from '../gridUtils'
import { getEnumValues } from 'model'
import { getAllVisibleLeafNodes } from '../getVisibleLeafNodes'
import { withRenderOnEvents } from '../withRenderOnEvents'
import { isNotNil } from 'utils/predicate'
import { createMakeTestId, OptionalTestProps } from '..'

interface FilteredCheckboxHeaderRendererProps extends IHeaderParams, OptionalTestProps {
  dataTestId?: string
  allPossibleStatuses: string[]
  getStatusAsString: (obj: unknown) => string
  getDerivedStatuses?: (gridApi: GridApi) => string[]
  getDerivedStatusesRowNodes?: (key: string, gridApi: GridApi) => IRowNode[]
  getSelectedDerivedStatus?: (gridApi: GridApi) => string | null
  withPortal?: boolean
}

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 55px;
  height: 100%;
  font-weight: ${constants.FontWeight.Bold};
  color: ${constants.CORE_PRIMARY_6};
`
const defaultDataTestId = 'FilteredCheckboxHeader'

export const FilteredCheckboxHeaderRendererComponent: React.FunctionComponent<FilteredCheckboxHeaderRendererProps> = ({
  api: gridApi,
  allPossibleStatuses,
  dataTestId = defaultDataTestId,
  getStatusAsString,
  getDerivedStatuses,
  getDerivedStatusesRowNodes,
  getSelectedDerivedStatus,
  withPortal
}) => {
  const makeTestId = createMakeTestId(dataTestId)
  const getAvailableAndCurrentStatuses: () => [Set<string>, string | null] = () => {
    const availableStatuses = new Set<string>([Locale.translate(Translation.TC_ALL), Locale.translate(Translation.TC_NONE)])
    let currentStatus: string | null = null
    let allSelected = true
    let hasLeafNodes = false

    const selectedNodes = gridApi.getSelectedNodes()

    currentStatus = getStatusAsString(selectedNodes.find(rowNode => !rowNode.group)?.data)

    if (getSelectedDerivedStatus) {
      const derivedStatus = getSelectedDerivedStatus(gridApi)
      if (derivedStatus) {
        currentStatus = derivedStatus
      }
    }

    getAllVisibleLeafNodes(gridApi).forEach((leafNode: TypedRowNode<unknown>) => {
      hasLeafNodes = true

      const status = getStatusAsString(leafNode.data)
      if (status) {
        availableStatuses.add(status)
      }

      if (!leafNode.isSelected()) {
        allSelected = false
      }
    })

    if (getDerivedStatuses) {
      const derivedStatuses = getDerivedStatuses(gridApi)
      derivedStatuses.forEach(s => availableStatuses.add(s))
    }

    if (selectedNodes.length === 0) {
      currentStatus = Locale.translate(Translation.TC_NONE)
    }

    if (allSelected && hasLeafNodes) {
      currentStatus = Locale.translate(Translation.TC_ALL)
    }

    return [availableStatuses, currentStatus]
  }

  const handleCheckboxChange = (checked: boolean) => {
    if (checked) {
      setNodesSelected(getAllVisibleLeafNodes(gridApi), true)
    } else {
      gridApi.deselectAll()
    }
  }

  const handleSelectionChange = (key: string) => {
    if (key === Locale.translate(Translation.TC_ALL)) {
      setNodesSelected(getAllVisibleLeafNodes(gridApi), true)
      return
    }

    if (getDerivedStatusesRowNodes) {
      const rowNodes = getDerivedStatusesRowNodes(key, gridApi)
      if (rowNodes.length) {
        gridApi.deselectAll()
        setNodesSelected(rowNodes, true)
        return
      }
    }

    if (key === Locale.translate(Translation.TC_NONE)) {
      gridApi.deselectAll()
      return
    }

    gridApi.deselectAll()
    setNodesSelected(
      getAllVisibleLeafNodes(gridApi).filter(node => getStatusAsString(node.data) === key),
      true
    )
  }

  let allStatuses = getEnumValues(allPossibleStatuses)
  let [availableAndCurrentStatuses, currentSelection] = getAvailableAndCurrentStatuses()

  const isPartialSelection =
    (isNotNil(currentSelection) &&
      ![Locale.translate(Translation.TC_ALL), Locale.translate(Translation.TC_NONE)].includes(currentSelection)) ||
    (currentSelection === Locale.translate(Translation.TC_ALL) && gridApi.getDisplayedRowCount() < gridApi.getModel().getRowCount())

  return (
    <Tooltip tooltip={Locale.translate(Translation.SC_SELECT_BY_STATUS)}>
      <Container>
        <Checkbox
          isChecked={currentSelection === Locale.translate(Translation.TC_ALL)}
          isPartialChecked={isPartialSelection}
          onUpdate={handleCheckboxChange}
          dataTestId={makeTestId('Selector')}
        />
        <Selectbox
          value={currentSelection}
          getOptionDisabled={option => !availableAndCurrentStatuses.has(option)}
          onUpdate={handleSelectionChange}
          optionKeys={allStatuses}
          hideInput={true}
          dataTestId={makeTestId('Options')}
          withPortal={withPortal}
        />
      </Container>
    </Tooltip>
  )
}

export const FilteredCheckboxHeaderRenderer = withRenderOnEvents(FilteredCheckboxHeaderRendererComponent, [
  'selectionChanged',
  'filterChanged',
  'rowDataUpdated'
])
